import { RECORD_STATUS_LABELS, RECORD_STATUS_TONE, type RecordStatus } from '@qms360/shared';
import { t } from '../../lib/i18n.js';

/**
 * A derived figure — ADR-0007, made visible.
 *
 * The single component every screen uses to show a calculated value, so the two
 * rules cannot be forgotten in one place and honoured in another:
 *
 *   1. **Null renders as an em dash, never as 0.** A metric that does not apply
 *      this period is not a metric scoring zero, and 0% reads as total failure.
 *   2. **The explanation travels with the figure.** The calculation helpers
 *      already return it, so the reader can always find out why a number is what
 *      it is without asking anyone.
 *
 * It is also read-only by construction. There is no editable variant, because a
 * derived value is never typed in.
 */
export function DerivedFigure({
  label,
  value,
  suffix = '',
  explanation,
  size = 'normal',
}: {
  label: string;
  /** The stored value, or null when the zero-denominator rule says not applicable. */
  value: string | number | null;
  suffix?: string;
  explanation?: string | null;
  size?: 'normal' | 'large';
}) {
  const isNotApplicable = value === null || value === undefined || value === '';
  const describedBy = explanation ? `${label.replace(/\s+/g, '-').toLowerCase()}-explanation` : undefined;

  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-content-secondary">
        {label}
      </dt>
      <dd
        className={`m-0 mt-1 font-semibold text-content-primary ${size === 'large' ? 'text-3xl' : 'text-lg'}`}
        aria-describedby={describedBy}
        title={explanation ?? undefined}
      >
        {isNotApplicable ? (
          <span className="text-content-secondary" aria-label={t("Not applicable")}>
            —
          </span>
        ) : (
          <>
            {value}
            {suffix}
          </>
        )}
        {/* Inside the <dd>: a description list allows only terms and
            descriptions in its groups (pass 11, axe definition-list). */}
        {explanation && (
          <span id={describedBy} className="mt-1 block text-xs font-normal text-content-secondary">
            {explanation}
          </span>
        )}
      </dd>
    </div>
  );
}

/** Month-over-month movement. No prior period is not a movement of zero. */
export function VarianceBadge({ value }: { value: string | null }) {
  if (value === null) {
    return (
      <span className="text-xs text-content-secondary">{t("No prior period to compare")}</span>
    );
  }
  const numeric = Number(value);
  const positive = numeric >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium ${
        positive
          ? 'bg-tone-positive-surface text-tone-positive'
          : 'bg-tone-critical-surface text-tone-critical'
      }`}
    >
      {/* Directional, so it flips under RTL (ADR-0014). */}
      <span aria-hidden className="rtl-flip">
        {positive ? '▲' : '▼'}
      </span>
      {positive ? '+' : ''}
      {value}{' '}{t("pts")}
    </span>
  );
}

const TONE_CLASS: Record<string, string> = {
  neutral: 'bg-tone-neutral-surface text-content-secondary',
  // Text shades that reach 4.5:1 on their surfaces (pass 11).
  pending: 'bg-tone-pending-surface text-tone-pending-text',
  info: 'bg-tone-info-surface text-tone-info-text',
  positive: 'bg-tone-positive-surface text-tone-positive-text',
};

/** Status chip using the shared tone mapping, so all three apps agree. */
export function StatusChip({ status }: { status: RecordStatus }) {
  return (
    <span
      className={`inline-block rounded-sm px-2 py-0.5 text-xs font-medium ${
        TONE_CLASS[RECORD_STATUS_TONE[status]] ?? TONE_CLASS.neutral
      }`}
    >
      {t(RECORD_STATUS_LABELS[status])}
    </span>
  );
}
